/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import PropTypes from 'prop-types';
import { useForm, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import makeStyles from '@mui/styles/makeStyles';
import { Button } from '@mui/material';
import { InputLabel, Textarea, RadioGroup, CheckboxesGroup, Select, MultipleCheckboxesSelect } from 'components/elements';
import { useFetchGenderPreference, useFetchPresentingIssues, useFetchServiceLanguage, useFetchTypesOfService, useFetchConfiguration } from 'hooks';
import Styles from './styles';

const useStyles = makeStyles(Styles);

function ServiceDetailsForm({ onSubmit, formId, submitRef, ...restProps }) {
  const classes = useStyles();
  const { t } = useTranslation('labels');
  const { isEnabledField, isRequiredField } = useFetchConfiguration();
  const { data: genderPrefOptions } = useFetchGenderPreference();
  const { data: presentingIssuesOptions } = useFetchPresentingIssues();
  const { data: serviceLangsOptions } = useFetchServiceLanguage();
  const { data: typesOfService } = useFetchTypesOfService();

  const { handleSubmit, control, getValues } = useForm({
    mode: 'onSubmitForm',
    defaultValues: restProps,
  });

  const onSubmitForm = (goBack) => {
    const values = getValues();
    onSubmit(values, goBack);
  };

  return (
    <div className={classes.clientInfoFormWrapper}>
      {!restProps.isServiceRequest && <h1 className={classes.formHeader}>{t('service_details')}</h1>}
      <form id={formId} className={classes.serviceDetailsForm} onSubmit={handleSubmit(() => onSubmitForm(false))}>
        {!restProps.isServiceRequest && isEnabledField('healthcareServiceId') && (
          <div className={classes.serviceType}>
            <Controller
              name="healthcareServiceId"
              control={control}
              rules={{
                required: {
                  value: isRequiredField('healthcareServiceId'),
                  message: t('please_fill_in_this_field'),
                },
              }}
              render={({ field: { ref, ...restField }, fieldState: { error } }) => (
                <Select
                  options={typesOfService}
                  label={<InputLabel text={t('type_of_service_requested')} required={isRequiredField('healthcareServiceId')} />}
                  helperText={error ? error.message : null}
                  error={!!error}
                  {...restField}
                />
              )}
            />
          </div>
        )}
        {isEnabledField('serviceLanguages') && (
          <div className={classes.serviceLanguage}>
            <Controller
              name="serviceLanguages"
              control={control}
              rules={{
                required: {
                  value: isRequiredField('serviceLanguages'),
                  message: t('please_fill_in_this_field'),
                },
              }}
              render={({ field: { ref, ...restField }, fieldState: { error } }) => (
                <MultipleCheckboxesSelect
                  options={serviceLangsOptions}
                  error={!!error}
                  helperText={error ? error.message : t('service_language_label')}
                  label={<InputLabel text={t('service_language')} required={isRequiredField('serviceLanguages')} />}
                  {...restField}
                />
              )}
            />
          </div>
        )}
        {isEnabledField('presentingIssueIds') && (
          <div className={classes.presentingIssues}>
            <Controller
              name="presentingIssueIds"
              control={control}
              rules={{
                required: {
                  value: isRequiredField('presentingIssueIds'),
                  message: t('please_fill_in_this_field'),
                },
              }}
              render={({ field: { ref, ...restField }, fieldState: { error } }) => (
                <CheckboxesGroup
                  options={presentingIssuesOptions}
                  error={!!error}
                  helperText={error ? error.message : null}
                  label={<InputLabel text={t('presenting_issues_label')} required={isRequiredField('presentingIssueIds')} />}
                  {...restField}
                  required={isRequiredField('presentingIssueIds')}
                />
              )}
            />
            <Controller
              name="issueDescription"
              control={control}
              render={({ field: { ref, ...restField } }) => (
                <Textarea
                  label={<InputLabel text={t('please_provide_a_brief_description_of_the_current_presenting_issue')} />}
                  helperText={t('this_information_helps_us_match_you_with_a_suitable_professional')}
                  limit={500}
                  {...restField}
                />
              )}
            />
          </div>
        )}
        {isEnabledField('preferredProviderGender') && (
          <div className={classes.genderPreference}>
            <Controller
              name="preferredProviderGender"
              control={control}
              rules={{
                required: {
                  value: isRequiredField('preferredProviderGender'),
                  message: t('please_fill_in_this_field'),
                },
              }}
              render={({ field: { ref, ...restField }, fieldState: { error } }) => (
                <RadioGroup
                  options={genderPrefOptions}
                  error={!!error}
                  helperText={error ? error.message : null}
                  {...restField}
                  label={<InputLabel text={t('gender_preference')} required={isRequiredField('preferredProviderGender')} />}
                  required={isRequiredField('preferredProviderGender')}
                />
              )}
            />
          </div>
        )}

        <div className={classes.controlButton}>
          {!restProps.isInitialStep && restProps.isServiceRequest && (
            <Button onClick={() => onSubmitForm(true)} ref={submitRef} variant="outlined" color="primary" size="large" className={classes.backButton}>
              {t('back')}
            </Button>
          )}
          {restProps.isServiceRequest && (
            <Button type="submit" variant="contained" color="primary" size="large">
              {t('next')}
            </Button>
          )}
        </div>
      </form>
    </div>
  );
}

ServiceDetailsForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  submitRef: PropTypes.objectOf(PropTypes.any).isRequired,
  formId: PropTypes.string.isRequired,
  preferredProviderGender: PropTypes.string,
  healthcareServiceId: PropTypes.string,
  serviceLanguages: PropTypes.arrayOf(PropTypes.any),
  issueDescription: PropTypes.string,
  presentingIssueIds: PropTypes.arrayOf(PropTypes.any),
};

ServiceDetailsForm.defaultProps = {
  preferredProviderGender: '',
  healthcareServiceId: '',
  serviceLanguages: [],
  issueDescription: '',
  presentingIssueIds: [],
};

export default ServiceDetailsForm;
